/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import { countBy, sortBy } from 'utils/array';
import { inRange, round } from 'utils/number';

/**
 * Represents all of the available colors for KCF designs
 */
export enum KCFColor {
    primaryText = '#273750',
    secondaryText = '#6c7c97',

    dashboardAxisTextDarker = '#485873',
    dashboardAxisTextLighter = '#c4cbdc',

    warningText = '#ce5d00',
    primaryYellow = '#F9A600',
    primaryRed = '#FF5252',
    primaryGreen = '#5AD6B0',

    editingTabWarning = '#F56600',

    offWhite1 = '#fafafc',
    lightGrey1 = '#f1f2f6',
    lightGrey2 = '#e9ecf2',
    lightGrey3 = '#dbe0e9',
    lightGrey4 = '#dfe1e6',
    mediumGrey4 = '#95a7c1',
    mediumGrey5 = '#6c7c97',
    mediumGrey6 = '#2d3c51',
    mediumGrey7 = '#262f3f',
    darkGrey7 = '#232b3b',
    darkGrey8 = '#1b212c',
    darkGrey9 = '#12161e',
    blueGrey = '#273D5A',
    whiteGrey = '#EBECF0',
    divider = '#CACACA',

    blue1 = '#e7edf7',
    blue2 = '#bdd2ef',
    blue3 = '#78a7ea',
    blue4 = '#78a7ea',
    blue5 = '#4470db',
    blue6 = '#3f66c7',
    blue7 = '#22388a',
    blue8 = '#2e488c',
    blue9 = '#233568',
    blue10 = '#1e2c56',

    darkBlue = '#091E42',

    orange1 = '#f1a71f',
    orange2 = '#ffa500',
    orange3 = '#ffc400',
    orange4 = '#FF8B00',
    orange5 = '#FFFAE6',
    orange6 = '#342f1e',

    green1 = '#288d4a',
    green2 = '#36B37E',
    green3 = '#33BB61',

    red1 = '#ff1537',
    red2 = '#ffc4cd',
    red3 = '#ff4f69',
    red4 = '#BF2600',
    red5 = '#EC0032',
    strongRedDark = '#c31723',

    white = '#fff',
    black = '#000',

    // Not sure if this is what these colors should be called, but they are very stand-out colors.
    strongRed = red1,
    lightRed = '#f3cbca', // Same hue as red1, same saturation and lightness as lightGreen. *kinda, mostly
    darkRed = '#8a424c', // Same hue as red1, same saturation and lightness as darkGreen. *kinda, mostly
    strongGreen = '#38ca69',
    lightGreen = '#c3e6cb',
    darkGreen = '#427b6c',
    successToast = '#D9F9E8',

    purple1 = '#5243AA',

    /**
     * Color of the landing page / overview task.
     */
    taskLaunch = '#4470db',
    tasksd3Primary = '#20aac5',
    taskOverviewPrimary = '#4470db',
    taskOverviewSecondary = '#395cb3',
    taskDashboardPrimary = '#c40be5',
    taskDashboardSecondary = '#9a08b4',
    taskWorkbenchPrimary = '#20aac5',
    taskWorkbenchSecondary = '#0f87a3',
    taskDeskPrimary = '#ea4e11',
    taskDeskSecondary = '#bf2c04',
    taskReportPrimary = '#10af75',
    taskReportSecondary = '#057c57',
    taskSettingsPrimary = '#f9a600',
    taskSettingsSecondary = '#dd8e03',
    taskHardwarePrimary = '#d10d54',
    taskHardwareSecondary = '#a00846',
    taskComponentsPrimary = '#ea4e11',
    taskComponentsSecondary = '#bf2c04',
    taskMapPrimary = '#ea0e47',
    taskMapSecondary = '#ba082a',
    issueUnboundFromGroup = strongRed,
    issueDisabledHeaderIconButtonColor = '#a5adba',

    /**
     * Color of the navigation drawer.
     */
    primaryNavigationBackground = darkGrey8,
    primaryNavigationHover = darkGrey7,
    secondaryNavigationBackground = blueGrey,
    secondaryNavigationSelected = '#202D3F',
    footerBackground = '#273142',

    /**
     * Issue Severity colors
     */
    criticalSeverity = '#D2001B',
    alarmSeverity = '#ff1537',
    moderateSeverity = '#f57600',
    lowSeverity = orange2,

    /**
     * Issue Status colors
     */
    draftStatus = '#F5F5F5',
    newStatus = '#008DA6',
    openStatus = green2,
    closedStatus = '#1B212C',
    wontFixStatus = '#365AAF',
    notAnIssueStatus = '#6C7C97',
    removedStatus = red4,

    /**
     * Issue Priority colors
     */
    priority1 = blueGrey,
    priority2 = mediumGrey5,
    priority3 = mediumGrey4,
    priority4 = '#dfe1e6',
    priority5 = lightGrey2,

    /**
     * Issue Resolution colors
     */
    resolved = '#5243AA',
    resolutionRemoved = '#FF1537',

    savingsGreen = '#00875a',

    /**
     * The colors of Chart series
     */
    chartLegendText = '#74839c',

    // Timeseries colors
    /**
     * Color of a timeseries spectrum line (frequency/time).
     */
    timeseriesSpectrum = '#00488b',
    /**
     * Color of a timeseries baseline (frequency/time).
     */
    timeseriesBaseline = '#A781A1',
    /**
     * Color of timeseries axes (X and Y).
     */
    timeseriesAxis = '#bdbdbd',
    /**
     * Inverted color of timeseries axes (X and Y).
     */
    timeseriesAxisReverse = '#424242',
    /**
     * Color of timeseries grid lines (X and Y).
     */
    timeseriesGrid = '#f3f3f3',
    /**
     * Inverted color of timeseries grid lines (X and Y).
     */
    timeseriesGridReverse = '#0c0c0c',

    /**
     * The color of anything that looks and acts like a link.
     */
    linkDefault = '#4470db',
    linkShiftedDefault = '#4f81ff', // For Dark Mode
    linkHover = '#395cb3',
    linkLight = '#e7edf7', // Is this a standard color?
    favorite = '#f29d01',

    /**
     * Color associated with confirmation check/icon.
     */
    confirmGreen = strongGreen,

    // Asset related colors
    /**
     * Color associated with an asset that is monitored.
     */
    assetMonitored = blue5,
    /**
     * Color associated with an asset that is in a normal state.
     */
    assetNormal = strongGreen,
    /**
     * Color associated with an asset that is in a warning state.
     */
    assetWarning = favorite,
    /**
     * Color associated with an asset that is in an alarm state.
     */
    assetAlarm = strongRed,
    /**
     * Color associated with an asset that is in a critical state.
     */
    assetCritical = '#d2001b',

    /**
     * Color associated with power bi
     */
    pbiOrange = orange1,

    /**
     * Color associated with custom icons for custom statuses
     */
    customIcon = '#232b3b',

    // TODO: Not sure what color this should be, like if it should be the
    // same color as anything else.
    checkbox = '#5b7dff',

    alarmTooltipRow = '#42384e',
    warningTooltipRow = '#42464d',
    acknowledgmentTooltipRow = '#2f415f',

    /**
     * Primary button background color
     */
    defaultButton = linkDefault,
    /**
     * Primary button hovered background color
     */
    defaultButtonHover = linkHover,
    /**
     * Primary button text color
     */
    defaultButtonTextColor = white,

    treeGroupIconColor = '#efdbba',

    notificationPrimaryOrange = '#e73c14',
    notificationSecondaryOrange = '#ee775b',

    faultTrianglePurple = '#7E01FF',

    /**
     * Custom background color for marker table
     */
    markerTableSelectedBackgroundColor = lightGrey1,
    /**
     * Custom border color for marker table
     */
    markerTableSelectedBorderColor = '#446FDB',
    /**
     * Default color for the folder icons in Folder Library
     */
    defaultFolder = '#F8D775',
    multiselectLozengeText = '#333',

    /**
     * not vetted by design
     */
    lozengeXBackgroundHover = 'rgb(255, 189, 173)',
    /**
     * not vetted by design
     */
    lozengeXIconHover = 'rgb(222, 53, 11)',

    /**
     * Color of the hardware status action button
     * Based on the darkBlue color with an opacity of 0.04
     */
    hardwareStatusActionButtonBackground = 'rgba(9, 30, 66, 0.04)',
    /**
     * Color of the hardware status action button hover
     * Based on the darkBlue color with an opacity of 0.08
     */
    hardwareStatusActionButtonBackgroundHover = 'rgba(9, 30, 66, 0.08)',
}

export enum AnalysisMarkerColors {
    Ruler = '#9e9e9e',
    '1x' = '#9c27b0',
    BPFO = '#f44336',
    BPFI = '#3f51b5',
    BSF = '#009688',
    FTF = '#ff9800',
    '2xLF' = '#03a9f4',
    'LF' = '#007D1C',
    'PPF' = '#C3094D',
    'RBF' = '#0BA3D3',
    'SSPF' = '#EC7100',
}

export enum IndicatorThresholdColors {
    off = KCFColor.mediumGrey4,
    alarm = KCFColor.assetAlarm,
    warning = KCFColor.assetWarning,
    criticalAlarm = KCFColor.assetCritical,
}

export enum HealthRangeColors {
    unspecified = KCFColor.mediumGrey4,
    off = KCFColor.blueGrey,
    healthy = '#4CC270',
    faultDeveloping = '#FFB430',
    unhealthy = '#FF1337',
}

export enum TrendLineColors {
    trendLine1 = '#4470DB',
    trendLine2 = '#FFC107',
    trendLine3 = '#D10D54',
    trendLine4 = '#6F3FC9',
    trendLine5 = '#1DE9B6',
    trendLine6 = '#C750DF',
    trendLine7 = '#009BE1',
    trendLine8 = '#48A748',
    trendLine9 = '#1740D9',
    trendLine10 = '#F29D01',
    trendLine11 = '#CEB900',
    trendLine12 = '#9C27B0',
    trendLine13 = '#00BCD4',
    trendLine14 = '#C2D035',
    trendLine15 = '#FE5918',
    trendLine16 = '#52DEF0',
    trendLine17 = '#FF80AB',
    trendLine18 = '#8BC34A',
    trendLine19 = '#EA80FC',
    trendLine20 = '#006F7D',
}

export const TrendLineColorsList = Object.values(TrendLineColors);

export enum ActivityLineColors {
    Alarm = '#ff1537',
    Warning = '#f29d01',
    Manual = '#0f48cd',
    Automatic = '#6c7c97',
    MachineIQ = '#39116d',
    MultipleActivities = '#0193a0',
    CreationMarker = '#2d3c51',
    CriticalAlarm = '#d2001b',
}

/**
 * Gets a distinct color for a series based on its index.
 * @param index The index of the series
 */
export function getTrendLineColorForIndex(index: number) {
    return TrendLineColorsList[index % Object.values(TrendLineColors).length];
}

/**
 * Calculates the next trend color based on the current selection colors.
 * The next trend color is either the color that has been used the least
 * or has the smallest positional index in the list of trend colors.
 *
 * @param currentColors The current color selections.
 */
export function getNextTrendLineColor(currentColors: string[]): string {
    if (currentColors.length === 0) {
        return TrendLineColorsList[0];
    }

    const colorCounts = TrendLineColorsList.map((color, index) => ({
        color,
        index,
        count: countBy(currentColors, (current) => current === color),
    }));

    const sortedCounts = sortBy(
        colorCounts,
        (count) => count.count,
        (count) => count.index
    );

    return sortedCounts[0].color;
}

/**
 * Return a color which correlates to the severity of the performance score. The performance score
 * should range from 0 to 1, inclusive.
 * @param score The performance score in the range [0, 1] to give a color for.
 */
export function getPerformanceScoreColor(score: number) {
    // Clamp the score to the range [0, 1]
    const clampedScore = Math.min(1, Math.max(0, score));

    if (clampedScore !== score) {
        // eslint-disable-next-line no-console
        console.error(
            `Expected a performance score in the range [0, 1], but got a value outside of the range: '${score}'`
        );
    }

    if (score < 0.6) {
        return KCFColor.strongGreen;
    }
    if (score < 0.9) {
        return KCFColor.favorite;
    }
    return KCFColor.strongRed;
}

/**
 * given a data number, returns the color the bar should be according to the scale:
 * green = 0 - 5.9
 * yellow = 6.0 - 7.9
 * red = 8+
 * @param data bar data to find the range of
 */
export const getPerformanceComparisonColor = (data: number) => {
    const fixedData = round(data, 2);
    if (inRange(fixedData, 0, 5.99)) {
        return KCFColor.strongGreen;
    }
    if (inRange(fixedData, 6, 7.99)) {
        return KCFColor.assetWarning;
    }

    return KCFColor.assetAlarm;
};
